// Change #100666 - "we need a company specific version so the admin of they
// can see the sims and status".
//
// Settings > Company Settings > SIM Connectivity: the mobile-data SIMs our
// Super Admin has assigned to this company (companysim/list), and the same SIM
// modal as Super Admin - same live status (online, 4G, usage) through
// companysim/live - without its admin buttons. The company can allocate a SIM
// to one of its own staff (companysim/allocate). Activating, suspending and
// moving between companies stay with us. Nothing here names the provider.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Input,
  Spinner,
  Table,
} from "reactstrap";
import SettingsPanel from "../../components/SettingsPanel";
import SimDetailModal from "../../SuperAmin/MasterSim/SimDetailModal";
import { simType, statusColor } from "../../SuperAmin/MasterSim/simHelpers";
import {
  getCompanySimList,
  getCompanySimLive,
  getCompanySimStaff,
  postCompanySimAllocate,
} from "../../../../helpers/fakebackend_helper";

const replaceRow = (setSims, row) => {
  if (!row?.Id) return;
  setSims((prev) =>
    prev.map((s) => (String(s.Id) === String(row.Id) ? row : s))
  );
};

const CompanySims = () => {
  const [sims, setSims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [detailSimId, setDetailSimId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const res = await getCompanySimList();
    setLoading(false);
    if (res?.status === 200) {
      setSims(res.companysim_list || []);
    } else {
      setError(res?.message || "Could not load your SIMs");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Staff for "Allocate to" - loaded once, the first time a SIM is opened.
  const [staffOptions, setStaffOptions] = useState(null);
  useEffect(() => {
    if (!detailSimId || staffOptions !== null) return;
    (async () => {
      const res = await getCompanySimStaff();
      setStaffOptions(res?.status === 200 ? res.staff_list || [] : []);
    })();
  }, [detailSimId, staffOptions]);

  const allocate = async (staffId) => {
    const res = await postCompanySimAllocate({
      Sim_Id: detailSimId,
      Staff_Id: staffId,
    });
    if (res?.status === 200) {
      toast.success(res?.message || "Saved");
      replaceRow(setSims, res.companysim_data);
    } else {
      toast.error(res?.message || "Could not allocate the SIM");
    }
  };

  const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const filtered = words.length
    ? sims.filter((item) => {
        const haystack = [
          item.Label,
          item.Iccid,
          item.Msisdn,
          item.Imsi,
          item.StatusDescription,
          item.CurrentStaffName,
          simType(item).kind || "",
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return words.every((w) => haystack.includes(w));
      })
    : sims;

  const detailSim = detailSimId
    ? sims.find((s) => String(s.Id) === String(detailSimId)) || null
    : null;

  return (
    <SettingsPanel
      title="SIM Connectivity"
      description="The mobile-data SIMs assigned to your company. Open one to see whether it is online, which network it is on and how much data it has used, and allocate it to a member of staff. To activate, suspend or return a SIM, contact support."
      actions={
        <Button
          color="light"
          className="border"
          onClick={load}
          disabled={loading}
        >
          <i className="ri-refresh-line align-bottom me-1" /> Refresh
        </Button>
      }
    >
      {detailSim && (
        <SimDetailModal
          sim={detailSim}
          onClose={() => setDetailSimId(null)}
          onRefreshed={(res) => replaceRow(setSims, res?.companysim_data)}
          loadLive={getCompanySimLive}
          companyView
          staffOptions={staffOptions}
          onAllocate={allocate}
        />
      )}
      <Card>
        <CardBody>
          <div className="mb-3" style={{ maxWidth: 360 }}>
            <Input
              type="search"
              bsSize="sm"
              placeholder="Search label, number, person..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {error ? (
            <div className="alert alert-warning mb-0">{error}</div>
          ) : (
            <div className="table-responsive">
              <Table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Label</th>
                    <th>Number (MSISDN)</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Used by</th>
                    <th>Last updated</th>
                    <th className="text-end"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading && sims.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-4">
                        <Spinner size="sm" className="me-2" />
                        Loading SIMs...
                      </td>
                    </tr>
                  ) : filtered.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-4">
                        {sims.length === 0
                          ? "No SIMs are assigned to your company."
                          : `No SIMs match "${search.trim()}".`}
                      </td>
                    </tr>
                  ) : (
                    filtered.map((item) => {
                      const type = simType(item);
                      return (
                        <tr
                          key={item.Id}
                          role="button"
                          onClick={() => setDetailSimId(item.Id)}
                        >
                          <td className="fw-medium">
                            {item.Label || (
                              <span className="text-muted">No label</span>
                            )}
                            {item.Iccid && (
                              <div className="small text-muted font-monospace">
                                {item.Iccid}
                              </div>
                            )}
                          </td>
                          <td>
                            {item.Msisdn || (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td>
                            {type.kind === "esim"
                              ? "eSIM"
                              : type.kind === "physical"
                              ? "Physical SIM"
                              : "—"}
                          </td>
                          <td>
                            <Badge color={statusColor(item.StatusId)}>
                              {item.StatusDescription || "Unknown"}
                            </Badge>
                            {Number(item.FlagOnDevice) !== 1 && (
                              <div className="small text-danger">No device</div>
                            )}
                          </td>
                          <td>
                            {item.CurrentStaffName || (
                              <span className="text-muted">Nobody yet</span>
                            )}
                          </td>
                          <td className="small">
                            {item.LastSyncedDateTime || (
                              <span className="text-muted">Never</span>
                            )}
                          </td>
                          <td className="text-end">
                            <Button
                              color="soft-primary"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDetailSimId(item.Id);
                              }}
                            >
                              <i className="ri-pulse-line align-middle me-1"></i>
                              Live status
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>
    </SettingsPanel>
  );
};

export default CompanySims;
